import api from "./axios";

/* ---------------- Public endpoints ---------------- */
export const getBusinessPageContent = () => api.get("/api/business/page-content/");
export const getBusinessOfferings = (params) => api.get("/api/business/offerings/", { params });
export const getBusinessOffering = (slug) => api.get(`/api/business/offerings/${slug}/`);

export const getProducts = (params) => api.get("/api/products/", { params });
export const getProduct = (slug) => api.get(`/api/products/${slug}/`);
export const getProductCategories = () => api.get("/api/products/categories/");
export const getVendors = () => api.get("/api/products/vendors/");

export const getPartners = (params) => api.get("/api/partners/", { params });
export const getFeaturedPartners = (limit) => api.get("/api/partners/featured/", { params: limit ? { limit } : {} });

/* ---------------- Admin: Business Offerings ---------------- */
export const adminGetBusinessOfferings = (params) => api.get("/api/admin/business-offerings/", { params });
export const adminGetBusinessOffering = (id) => api.get(`/api/admin/business-offerings/${id}/`);
function offeringToFormData(data) {
  const fd = new FormData();
  ["title", "slug", "category", "short_description", "detailed_description", "order_index", "is_active", "meta_title", "meta_description"]
    .forEach((k) => { if (data[k] !== undefined && data[k] !== null) fd.append(k, data[k]); });
  // Only attach the file if the user actually picked a new one — an existing
  // string URL from a previous GET is not a valid upload and must not be resent.
  if (data.icon_or_image instanceof File) fd.append("icon_or_image", data.icon_or_image);
  return fd;
}

export const adminCreateBusinessOffering = (data) => api.post("/api/admin/business-offerings/", offeringToFormData(data));
export const adminUpdateBusinessOffering = (id, data) => api.put(`/api/admin/business-offerings/${id}/`, offeringToFormData(data));
export const adminDeleteBusinessOffering = (id) => api.delete(`/api/admin/business-offerings/${id}/`); // add — your API supports DELETE but nothing calls it yet

/* ---------------- Admin: Product Categories ---------------- */
export const adminGetProductCategories = (params) => api.get("/api/admin/product-categories/", { params });
export const adminGetProductCategory = (id) => api.get(`/api/admin/product-categories/${id}/`);
export const adminCreateProductCategory = (data) => api.post("/api/admin/product-categories/", data);
export const adminUpdateProductCategory = (id, data) => api.put(`/api/admin/product-categories/${id}/`, data);

/* ---------------- Admin: Vendors ---------------- */
function vendorToFormData(data) {
  const fd = new FormData();
  fd.append("name", data.name ?? "");
  if (data.website_url) fd.append("website_url", data.website_url);
  if (data.is_active !== undefined) fd.append("is_active", data.is_active);
  if (data.logo instanceof File) fd.append("logo", data.logo);
  return fd;
}
export const adminGetVendors = (params) => api.get("/api/admin/vendors/", { params });
export const adminGetVendor = (id) => api.get(`/api/admin/vendors/${id}/`);
export const adminCreateVendor = (data) => api.post("/api/admin/vendors/", vendorToFormData(data));
export const adminUpdateVendor = (id, data) => api.put(`/api/admin/vendors/${id}/`, vendorToFormData(data));
export const adminDeleteVendor = (id) => api.delete(`/api/admin/vendors/${id}/`);

/* ---------------- Admin: Products ---------------- */
function productToFormData(data) {
  const fd = new FormData();
  ["name", "slug", "product_type", "short_description", "detailed_description", "specifications",
    "order_index", "is_featured", "is_active", "meta_title", "meta_description"]
    .forEach((k) => { if (data[k] !== undefined && data[k] !== null && data[k] !== "") fd.append(k, data[k]); });
  // Backend expects category_id / vendor_id on write, even though GET returns
  // nested category / vendor objects. `data.category` / `data.vendor` here are
  // plain ids (from the form's select), so just rename them on the way out.
  if (data.category !== undefined && data.category !== "") fd.append("category_id", data.category);
  if (data.vendor !== undefined && data.vendor !== "") fd.append("vendor_id", data.vendor);
  if (data.image instanceof File) fd.append("image", data.image);
  return fd;
}
export const adminGetProducts = (params) => api.get("/api/admin/products/", { params });
export const adminGetProduct = (id) => api.get(`/api/admin/products/${id}/`);
export const adminCreateProduct = (data) => api.post("/api/admin/products/", productToFormData(data));
export const adminUpdateProduct = (id, data) => api.put(`/api/admin/products/${id}/`, productToFormData(data));
export const adminDeleteProduct = (id) => api.delete(`/api/admin/products/${id}/`);

/* ---------------- Admin: Partners ---------------- */
function partnerToFormData(data) {
  const fd = new FormData();
  fd.append("name", data.name ?? "");
  fd.append("partner_type", data.partner_type ?? "");
  if (data.order_index !== undefined && data.order_index !== "") fd.append("order_index", data.order_index);
  if (data.is_active !== undefined) fd.append("is_active", data.is_active);
  if (data.logo instanceof File) fd.append("logo", data.logo);
  return fd;
}
export const adminGetPartners = (params) => api.get("/api/admin/partners/", { params });
export const adminGetPartner = (id) => api.get(`/api/admin/partners/${id}/`);
export const adminCreatePartner = (data) => api.post("/api/admin/partners/", partnerToFormData(data));
export const adminUpdatePartner = (id, data) => api.put(`/api/admin/partners/${id}/`, partnerToFormData(data));
export const adminDeletePartner = (id) => api.delete(`/api/admin/partners/${id}/`);

/* ---------------- Admin: Business Page Content ---------------- */
export const adminGetBusinessPageContent = () => api.get("/api/admin/page-content/business/");
function pageContentToFormData(data) {
  const fd = new FormData();
  ["hero_title", "hero_subtitle", "teaser_text", "meta_title", "meta_description"]
    .forEach((k) => { if (data[k] !== undefined && data[k] !== null) fd.append(k, data[k]); });
  if (data.hero_background_image instanceof File) fd.append("hero_background_image", data.hero_background_image);
  if (data.scope_cards !== undefined && data.scope_cards !== null) fd.append("scope_cards", data.scope_cards);
  return fd;
}
export const adminUpdateBusinessPageContent = (data) => api.put("/api/admin/page-content/business/", pageContentToFormData(data));
/* ---------------- Admin: Business Inquiries ---------------- */
export const adminGetInquiries = (params) => api.get("/api/admin/inquiries/", { params });
export const adminGetInquiry = (id) => api.get(`/api/admin/inquiries/${id}/`);
export const adminUpdateInquiryStatus = (id, data) => api.patch(`/api/admin/inquiries/${id}/`, data);
export const adminAddInquiryNote = (id, data) => api.post(`/api/admin/inquiries/${id}/notes/`, data);


// No single-partner public endpoint exists — fetch the list and find by id.
export const getPartnerById = (id) => getPartners().then((r) => {
  const list = Array.isArray(r.data) ? r.data : r.data?.results ?? [];
  return list.find((p) => String(p.id) === String(id));
});